<template>
    <div v-if="!collapsed" class="grid-cell" :style="cellStyle" @click="$emit('click', $event)" @dblclick="$emit('dblclick', $event)">

        <div v-if="resizable" class="drag-handle" :class="{'drag-handle--visible': dragging}" style="left: 0;"
             draggable="true"
             @mousedown.prevent="resizeStart($event, 'left')"></div>
        <div v-if="resizable" class="drag-handle" :class="{'drag-handle--visible': dragging}" style="right: 0;"
             draggable="true"
             @mousedown.prevent="resizeStart($event, 'right')"></div>

        <slot></slot>
    </div>
</template>

<script>
export default {
    name: 'GridCell',
    props: {
        row: Number,
        rowTo: Number,
        colFrom: Number,
        colTo: Number,
        sticky: {
            type: Object,
            defaults: () => null,
        },
        resizable: {
            type: Boolean,
            defaults: () => false,
        },
        collapsed: {
            type: Boolean,
            defaults: () => false,
        }
    },
    data() {
        return {
            dragging: false,
            dragStartOrigin: null,
            dragStartCellWidth: null,
            origColFrom: null,
            origColTo: null,
            dragMoveEL: null,
            dragEndEL: null,
        }
    },
    computed: {
        cellStyle() {
            const style = {
                gridColumn: `${this.colFrom} / ${( this.colTo )}`,
                gridRow: `${this.row} / ${ this.rowTo ? this.rowTo : this.row + 1 }`,
            };

            if (this.sticky) {
                style.position = 'sticky';
                if (this.sticky.left != null) style.left = `${this.sticky.left}px`;
                if (this.sticky.right != null) style.right = `${this.sticky.right}px`;
                if (this.sticky.top != null) style.top = `${this.sticky.top}px`;
                if (this.sticky.bottom != null) style.bottom = `${this.sticky.bottom}px`;
            }

            return style;
        },
    },
    methods: {
        resizeStart(event, direction) {
            this.dragging = true;
            const bounds = this.$el.getBoundingClientRect();
            this.dragStartOrigin = direction === 'right' ? bounds.right : bounds.left;
            this.dragStartCellWidth = this.$el.offsetWidth / ( this.colTo - this.colFrom + 1 );
            this.origColFrom = this.colFrom;
            this.origColTo = this.colTo;

            this.dragMoveEL = (event) => this.resizeMove(event, direction);
            this.dragEndEL = (event) => this.resizeEnd(event, direction);
            document.addEventListener('mousemove', this.dragMoveEL);
            document.addEventListener('mouseup', this.dragEndEL);

            this.$emit('resizeStart', {
                event,
                direction,
                origin: this.dragStartOrigin,
                dragWidth: this.dragStartCellWidth,
            });
        },

        resizeMove(event, direction) {
            // Could also emit an event for both directions when alt-key is pressed ?
            // This would allow to resize both start and end of the cell at the same time
            if (!this.dragging) return;

            const dragDiff = event.clientX - this.dragStartOrigin;
            const draggedCellCount = dragDiff / this.dragStartCellWidth;
            const totalCellCount = direction === 'right'
                ? this.origColTo + 1 + draggedCellCount
                : this.origColFrom + draggedCellCount;
            const colFrom = direction === 'left' ? Math.floor(totalCellCount) : this.origColFrom;
            const colTo = direction === 'right' ? Math.ceil(totalCellCount) - 1 : this.origColTo;
            // console.log('dragging:', direction, colFrom, colTo);
            this.$emit('resizeMove', {
                event,
                direction,
                colFrom,
                colTo,
            });
        },

        resizeEnd(event, direction) {
            if (!this.dragging) return;

            this.dragging = false;
            const dragDiff = event.clientX - this.dragStartOrigin;
            const draggedCellCount = dragDiff / this.dragStartCellWidth;
            const totalCellCount = direction === 'right'
                ? this.colTo + 1 + draggedCellCount
                : this.colFrom + draggedCellCount;
            const colFrom = direction === 'left' ? Math.floor(totalCellCount) : this.origColFrom;
            const colTo = direction === 'right' ? Math.ceil(totalCellCount) - 1 : this.origColTo;
            this.$emit('resizeEnd', {
                event,
                direction,
                colFrom,
                colTo,
            });
            document.removeEventListener('mousemove', this.dragMoveEL);
            document.removeEventListener('mouseup', this.dragEndEL);
        },
    }
}
</script>

<style scoped>

.grid-cell {
    overflow: hidden;
    position: relative;
}

.drag-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: ew-resize;
    z-index: 1;
    opacity: 0;
    transition: opacity 125ms ease-in-out;
}

.drag-handle::before {
    content: '';
    display: block;
    height: calc(100% - 4px);
    width: 2px;
    margin: 2px;

    border: 1px solid var(--contrast-6);
    border-radius: 2px;
}

.grid-cell:hover > .drag-handle, .drag-handle:hover {
    opacity: 1;
}

</style>
